import { normalizeAttendeeEligibility } from '../../../shared/registrationSettings';
import {
  messageHasChatAttachments,
  normalizePartnerGroupChannelAttachments,
} from './communityChatAttachments';
import {
  buildPartnerCommunityCurrentUserIds,
  getPartnerCommunityDisplayName,
  mapMessageReactionFields,
} from './communityChatReactions';
import { ARIA_BOT_SENDER_ID, isAriaBotMessage } from './widgets/AriaBotAvatar';
import {
  getAriaMessageDataEnvelope,
  isAriaInlineRichMessage,
  isAriaRichCardMessage,
  normalizeAriaRichMessage,
} from '../../AriaHandsoff/ariaMessageTypes';
import { isSharedEventCardMessage, normalizeSharedEventMessage } from './sharedEventMessage';

const toTrimmedString = (value) => {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
};

/**
 * Chat bubble text from listing/socket payloads.
 * Notification envelopes often set `message` to "Name: text" — prefer plain text fields,
 * then strip a matching "Name: " prefix as a fallback.
 */
export const resolvePartnerGroupChannelMessageText = (payload = {}, sender = {}) => {
  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const resolvedSender =
    (sender && typeof sender === 'object' ? sender : null) ??
    (payload.sender && typeof payload.sender === 'object' ? payload.sender : null) ??
    {};

  const candidates = [payload.text, payload.messageText, payload.messagePreview, payload.message];

  let text = '';
  for (const candidate of candidates) {
    const value = String(candidate ?? '').trim();
    if (value) {
      text = value;
      break;
    }
  }

  if (!text) {
    return '';
  }

  const senderName =
    toTrimmedString(resolvedSender.fullName) ??
    toTrimmedString(resolvedSender.name) ??
    toTrimmedString(resolvedSender.displayName);

  if (!senderName) {
    return text;
  }

  const prefix = `${senderName}:`;
  if (
    text.length > prefix.length &&
    text.slice(0, prefix.length).toLowerCase() === prefix.toLowerCase()
  ) {
    return text.slice(prefix.length).trim();
  }

  return text;
};

export const buildPartnerChatSenderDefaults = (partnerProfile, firebaseUid) => ({
  fullName: partnerProfile?.fullName ?? null,
  avatarUrl: partnerProfile?.avatarUrl ?? null,
  uid: firebaseUid ?? partnerProfile?.uid ?? null,
});

const STAFF_SENDER_KEYS = new Set(['partner', 'aria', 'bot', 'admin', 'system']);

export const hasPartnerStaffSenderFlag = (source = {}) => {
  const sender = source.sender && typeof source.sender === 'object' ? source.sender : {};
  return (
    source.isPartner === true ||
    sender.isPartner === true ||
    Boolean(toTrimmedString(source.partnerProfileId ?? source.partner_profile_id)) ||
    Boolean(toTrimmedString(sender.partnerProfileId ?? sender.partner_profile_id))
  );
};

/** Map Stream-style sender fields (`name` / `image`) onto listing sender fields. */
export const normalizeChatSender = (sender) => {
  if (!sender || typeof sender !== 'object') {
    return {};
  }

  return {
    ...sender,
    id: toTrimmedString(sender.id) ?? sender.id,
    uid: toTrimmedString(sender.uid) ?? sender.uid,
    fullName:
      toTrimmedString(sender.fullName) ??
      toTrimmedString(sender.name) ??
      toTrimmedString(sender.displayName),
    avatarUrl:
      toTrimmedString(sender.avatarUrl) ??
      toTrimmedString(sender.image) ??
      toTrimmedString(sender.avatar) ??
      toTrimmedString(sender.photoURL) ??
      toTrimmedString(sender.photoUrl) ??
      toTrimmedString(sender.profileImageUrl) ??
      toTrimmedString(sender.profilePicture),
  };
};

export const inferChatMessageSource = (message = {}, sender = {}) => {
  if (sender.isAriaBot === true || message.isAriaBot === true) {
    return 'aria';
  }
  if (sender.isAdmin === true || message.isAdmin === true) {
    return 'admin';
  }
  if (hasPartnerStaffSenderFlag({ ...message, sender })) {
    return 'partner';
  }

  const explicit =
    toTrimmedString(message.source)?.toLowerCase() ?? toTrimmedString(sender.source)?.toLowerCase();
  if (explicit) {
    return explicit;
  }

  const role = String(sender.role ?? message.senderRole ?? message.senderType ?? sender.type ?? '')
    .trim()
    .toLowerCase();
  if (STAFF_SENDER_KEYS.has(role)) {
    return role === 'bot' ? 'aria' : role;
  }

  return null;
};

export const isPartnerChatSender = (message, { firebaseUid } = {}) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  if (message.isPartnerSender === true) {
    return true;
  }

  const sender = normalizeChatSender(message.sender ?? {});
  if (STAFF_SENDER_KEYS.has(String(message.source ?? '').toLowerCase())) {
    return true;
  }

  if (STAFF_SENDER_KEYS.has(String(sender.source ?? '').toLowerCase())) {
    return true;
  }

  if (hasPartnerStaffSenderFlag({ ...message, sender })) {
    return true;
  }

  const senderRole = String(sender.role ?? message.senderRole ?? '').toLowerCase();
  if (STAFF_SENDER_KEYS.has(senderRole)) {
    return true;
  }

  const senderType = String(message.senderType ?? sender.type ?? '').toLowerCase();
  if (STAFF_SENDER_KEYS.has(senderType)) {
    return true;
  }

  if (firebaseUid) {
    const senderUid = sender.uid ?? message.senderUid ?? null;
    if (senderUid && senderUid === firebaseUid) {
      return true;
    }
  }

  return false;
};

/** Shared mapper options for every partner group-channel message source (list API, socket, mutations). */
export const buildPartnerGroupChannelMessageMapOptions = ({
  firebaseUid,
  partnerProfile,
  senderDefaults,
} = {}) => {
  const resolvedSenderDefaults =
    senderDefaults ?? buildPartnerChatSenderDefaults(partnerProfile, firebaseUid);

  return {
    firebaseUid,
    senderDefaults: resolvedSenderDefaults,
    currentUserIds: buildPartnerCommunityCurrentUserIds({ firebaseUid, partnerProfile }),
    partnerDisplayName: getPartnerCommunityDisplayName(partnerProfile),
  };
};

const resolvePartnerGroupChannelMessageMapOptions = (options = {}) => {
  if (options.currentUserIds instanceof Set) {
    return options;
  }

  return buildPartnerGroupChannelMessageMapOptions(options);
};

/** Extract a listing-shaped message object from mutation/socket/API wrappers. */
export const extractPartnerGroupChannelApiMessage = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const layer = payload.data ?? payload;
  const nested = layer?.data ?? layer;

  if (nested?.message && typeof nested.message === 'object') {
    return nested.message;
  }

  if (layer?.message && typeof layer.message === 'object') {
    return layer.message;
  }

  if (nested?.id && (nested.text || nested.messageText)) {
    return nested;
  }

  if (layer?.id && (layer.text || layer.messageText)) {
    return layer;
  }

  return null;
};

/** Normalize any listing-shaped payload (socket, mutation, API) into listing message fields. */
export const toPartnerGroupChannelListingMessage = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  return {
    id: payload.id ?? payload.messageId ?? null,
    channelId: payload.channelId ?? null,
    text: resolvePartnerGroupChannelMessageText(payload, payload.sender ?? payload.user),
    data: payload.data ?? payload.custom ?? payload.messageData ?? null,
    attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
    originalAttachments: Array.isArray(payload.originalAttachments)
      ? payload.originalAttachments
      : [],
    sender: payload.sender ?? null,
    user: payload.user ?? null,
    createdAt: payload.createdAt ?? payload.created_at ?? null,
    updatedAt: payload.updatedAt ?? payload.updated_at ?? null,
    deletedAt: payload.deletedAt ?? payload.deleted_at ?? null,
    editedAt: payload.editedAt ?? payload.edited_at ?? null,
    source: payload.source ?? payload.sender?.source ?? null,
    replyToMessageId:
      payload.replyToMessageId ??
      payload.reply_to_message_id ??
      payload.parent_id ??
      payload.parentId ??
      null,
    forwardOf: payload.forwardOf ?? payload.forward_of ?? null,
    parentId:
      payload.parent_id ??
      payload.parentId ??
      payload.replyToMessageId ??
      payload.reply_to_message_id ??
      null,
    underReview: payload.under_review ?? payload.underReview ?? false,
    moderation: payload.moderation ?? null,
    reaction_counts: payload.reaction_counts ?? payload.reactionCounts ?? null,
    reaction_scores: payload.reaction_scores ?? payload.reactionScores ?? null,
    reaction_groups: payload.reaction_groups ?? payload.reactionGroups ?? null,
    latest_reactions: payload.latest_reactions ?? payload.latestReactions ?? null,
    own_reactions: payload.own_reactions ?? payload.ownReactions ?? null,
  };
};

const resolveMessageEditedAt = (message) => {
  const explicit = message.editedAt ?? message.edited_at ?? null;
  if (explicit) {
    return explicit;
  }

  const createdAt = message.createdAt ?? message.created_at ?? null;
  const updatedAt = message.updatedAt ?? message.updated_at ?? null;
  if (createdAt && updatedAt && updatedAt !== createdAt) {
    return updatedAt;
  }

  return null;
};

const normalizeMessage = (message, index, options = {}) => {
  const listing = toPartnerGroupChannelListingMessage(message);
  if (!listing) return null;

  const text = String(listing.text ?? '').trim();
  const dataEnvelope = getAriaMessageDataEnvelope(listing) ?? getAriaMessageDataEnvelope(message);
  const ariaPayload = normalizeAriaRichMessage(dataEnvelope ?? {});
  const ariaMessageType = ariaPayload?.ariaMessageType ?? null;
  const ariaRenderMessage = { ariaMessageType, ariaPayload };
  const sharedEventPayload = normalizeSharedEventMessage(dataEnvelope ?? {});
  const hasRichCard = isAriaRichCardMessage(ariaRenderMessage);
  const hasSharedEventCard = isSharedEventCardMessage({ sharedEventPayload });
  const hasInlineAriaContent = isAriaInlineRichMessage(ariaRenderMessage);
  const normalizedAttachments = normalizePartnerGroupChannelAttachments(listing);
  const hasAttachments = normalizedAttachments.length > 0 || messageHasChatAttachments(message);

  // Skip empty text messages unless they carry a renderable rich card or attachments.
  if (!text && !hasRichCard && !hasSharedEventCard && !hasInlineAriaContent && !hasAttachments) {
    return null;
  }

  const mapOptions = resolvePartnerGroupChannelMessageMapOptions(options);
  const sender = normalizeChatSender(listing.sender ?? listing.user ?? message?.sender ?? {});
  const senderDefaults = mapOptions.senderDefaults ?? {};
  const createdAt = listing.createdAt ?? message?.time ?? new Date().toISOString();
  const senderId =
    toTrimmedString(sender.id) ??
    toTrimmedString(listing.user?.id) ??
    toTrimmedString(message?.senderId);
  // Never fall back to the signed-in admin/partner uid for member messages.
  // Partner/admin send responses may omit sender — then defaults are OK when source is partner/admin.
  const inferredSource = inferChatMessageSource({ ...listing, ...message, sender }, sender);
  const provisionalSource =
    inferredSource ??
    (String(senderId ?? '').toLowerCase() === ARIA_BOT_SENDER_ID ||
    String(
      toTrimmedString(sender.uid) ?? toTrimmedString(message?.senderUid) ?? '',
    ).toLowerCase() === ARIA_BOT_SENDER_ID
      ? 'aria'
      : null);
  const canUseSenderDefaultsEarly = ['partner', 'admin'].includes(
    String(provisionalSource ?? '').toLowerCase(),
  );
  const senderUid =
    toTrimmedString(sender.uid) ??
    toTrimmedString(message?.senderUid) ??
    (String(senderId ?? '').toLowerCase() === ARIA_BOT_SENDER_ID ? ARIA_BOT_SENDER_ID : null) ??
    (canUseSenderDefaultsEarly ? toTrimmedString(senderDefaults.uid) : null);
  const ariaBotMessage = isAriaBotMessage({
    senderUid,
    senderId,
    sender,
    user: listing.user,
  });
  const source = provisionalSource ?? (ariaBotMessage ? 'aria' : null);
  const fromSignedInUser =
    Boolean(mapOptions.firebaseUid) &&
    Boolean(senderUid) &&
    String(senderUid) === String(mapOptions.firebaseUid);
  const canUseSenderDefaults =
    fromSignedInUser || ['partner', 'admin'].includes(String(source ?? '').toLowerCase());

  const resolvedAvatarUrl = ariaBotMessage
    ? (sender.avatarUrl ?? listing.user?.image ?? message?.avatarUrl ?? null)
    : (sender.avatarUrl ??
      (canUseSenderDefaults ? senderDefaults.avatarUrl : null) ??
      listing.user?.image ??
      message?.avatarUrl ??
      null);

  return {
    id: listing.id ?? `message-${index}`,
    channelId: listing.channelId ?? null,
    author:
      sender.fullName ??
      (ariaBotMessage ? 'Aria' : null) ??
      (canUseSenderDefaults ? senderDefaults.fullName : null) ??
      toTrimmedString(listing.user?.name) ??
      toTrimmedString(listing.user?.fullName) ??
      message?.author ??
      'Member',
    avatarUrl: resolvedAvatarUrl || undefined,
    senderId: senderId ?? (ariaBotMessage ? ARIA_BOT_SENDER_ID : null),
    senderUid: ariaBotMessage ? ARIA_BOT_SENDER_ID : senderUid,
    source,
    time: createdAt,
    createdAt,
    updatedAt: listing.updatedAt ?? null,
    deletedAt: listing.deletedAt ?? null,
    attachments: normalizedAttachments,
    originalAttachments: listing.originalAttachments ?? [],
    replyToMessageId: listing.replyToMessageId ?? listing.parentId ?? null,
    forwardOf: listing.forwardOf ?? null,
    parentId: listing.parentId ?? listing.replyToMessageId ?? null,
    underReview: Boolean(listing.underReview),
    moderation: listing.moderation ?? null,
    reactionScores: listing.reaction_scores ?? {},
    editedAt: resolveMessageEditedAt(listing),
    text,
    data: dataEnvelope,
    ariaMessageType,
    ariaPayload,
    sharedEventPayload,
    isPartnerSender: isPartnerChatSender(listing, mapOptions) || ariaBotMessage || fromSignedInUser,
    ...mapMessageReactionFields(listing, {
      currentUserIds: mapOptions.currentUserIds,
      partnerDisplayName: mapOptions.partnerDisplayName,
    }),
  };
};

export const mapPartnerGroupChannelChatMessage = (message, index = 0, options = {}) =>
  normalizeMessage(message, index, options);

export const pickPartnerGroupChannelMessageReactionFields = (mapped) => {
  if (!mapped) {
    return null;
  }

  return {
    reactionCounts: mapped.reactionCounts,
    ownReactions: mapped.ownReactions,
    ownReaction: mapped.ownReaction,
    latestReactions: mapped.latestReactions,
    reactionChips: mapped.reactionChips,
  };
};

const listingHasReactionSignal = (listing) => {
  if (!listing || typeof listing !== 'object') {
    return false;
  }

  return (
    listing.reaction_counts != null ||
    listing.reactionCounts != null ||
    listing.reaction_scores != null ||
    listing.reactionScores != null ||
    listing.reaction_groups != null ||
    listing.reactionGroups != null ||
    listing.latest_reactions != null ||
    listing.latestReactions != null ||
    listing.own_reactions != null ||
    listing.ownReactions != null
  );
};

/** True when mapped reaction fields carry displayable reaction state. */
export const hasMeaningfulReactionFields = (reactionFields) => {
  if (!reactionFields || typeof reactionFields !== 'object') {
    return false;
  }

  if ((reactionFields.reactionChips ?? []).length > 0) {
    return true;
  }

  if ((reactionFields.ownReactions ?? []).length > 0 || reactionFields.ownReaction != null) {
    return true;
  }

  if ((reactionFields.latestReactions ?? []).length > 0) {
    return true;
  }

  return Object.keys(reactionFields.reactionCounts ?? {}).length > 0;
};

/** Map add/remove reaction API payloads into cache-ready reaction fields. */
export const mapPartnerGroupChannelApiReactionResponse = (data, mapOptions = {}) => {
  const resolvedOptions = resolvePartnerGroupChannelMessageMapOptions(mapOptions);
  const candidates = [
    extractPartnerGroupChannelApiMessage(data),
    data?.data?.message,
    typeof data?.message === 'object' ? data.message : null,
    data?.data,
    data,
  ].filter((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate));

  for (const candidate of candidates) {
    const listing = toPartnerGroupChannelListingMessage(candidate);
    if (!listingHasReactionSignal(listing)) {
      continue;
    }

    // Prefer full message mapping when text exists; otherwise map reaction fields only.
    if (String(listing.text ?? '').trim()) {
      const mapped = mapPartnerGroupChannelChatMessage(listing, 0, resolvedOptions);
      const reactionFields = pickPartnerGroupChannelMessageReactionFields(mapped);
      if (hasMeaningfulReactionFields(reactionFields)) {
        return reactionFields;
      }
    }

    const reactionFields = mapMessageReactionFields(listing, {
      currentUserIds: resolvedOptions.currentUserIds,
      partnerDisplayName: resolvedOptions.partnerDisplayName,
    });

    if (hasMeaningfulReactionFields(reactionFields)) {
      return reactionFields;
    }
  }

  // Status-only success bodies (e.g. `{ message: "Reaction updated" }`) must not wipe
  // optimistic chips — caller should keep the local cache update.
  return null;
};

/** Map a socket envelope payload into the same UI message shape as the messages listing API. */
export const mapPartnerGroupChannelSocketMessage = (envelope = {}, options = {}) => {
  const nested = envelope?.data ?? envelope?.payload;
  const body = nested && typeof nested === 'object' && !Array.isArray(nested) ? nested : envelope;
  const rawSender =
    (body.sender && typeof body.sender === 'object' ? body.sender : null) ??
    (envelope.sender && typeof envelope.sender === 'object' ? envelope.sender : null) ??
    (body.user && typeof body.user === 'object' ? body.user : null);
  const sender = normalizeChatSender(rawSender);
  const moderation =
    body.moderation && typeof body.moderation === 'object' ? body.moderation : null;

  const source = inferChatMessageSource({ ...envelope, ...body, sender }, sender);

  const listing = toPartnerGroupChannelListingMessage({
    ...body,
    id: body.messageId ?? body.id,
    text: resolvePartnerGroupChannelMessageText(body, sender),
    data: body.data ?? null,
    createdAt: body.createdAt ?? envelope.timestamp,
    source,
    sender: {
      ...sender,
      avatarUrl:
        sender.avatarUrl ??
        toTrimmedString(body.avatarUrl) ??
        toTrimmedString(body.user?.image) ??
        toTrimmedString(body.user?.avatarUrl) ??
        null,
    },
    underReview: moderation?.status === 'under_review' || body.underReview,
    moderation,
    replyToMessageId: body.replyToMessageId ?? body.parent_id ?? body.parentId ?? null,
    forwardOf: body.forwardOf ?? null,
    attachments: Array.isArray(body.attachments) ? body.attachments : [],
  });

  if (!listing?.id) {
    return null;
  }

  // Allow empty/null text when rich Aria cards or attachments are present.
  return mapPartnerGroupChannelChatMessage(listing, 0, options);
};

/** Map a socket reaction payload into reaction fields for cache updates. */
export const mapPartnerGroupChannelSocketReactionUpdate = (envelope = {}, options = {}) => {
  const body = envelope?.data ?? envelope?.payload ?? {};
  const messageId = String(body.messageId ?? body.id ?? '').trim();

  if (!messageId) {
    return null;
  }

  // Never trust socket `own_reactions`. Stream's own_reactions are for the reacting
  // client / server token, not this portal viewer — applying them marks someone
  // else's emoji as ours and disables it in the picker.
  const listing = toPartnerGroupChannelListingMessage({
    id: messageId,
    reaction_counts: body.reaction_counts,
    reaction_scores: body.reaction_scores,
    reaction_groups: body.reaction_groups,
    latest_reactions: body.latest_reactions,
  });

  const mapOptions = resolvePartnerGroupChannelMessageMapOptions(options);
  const reactionFields = mapMessageReactionFields(listing, {
    currentUserIds: mapOptions.currentUserIds,
    partnerDisplayName: mapOptions.partnerDisplayName,
  });

  return {
    messageId,
    ...reactionFields,
  };
};

export const extractPartnerGroupChannelRoot = (payload) => {
  const root = payload?.data ?? payload;
  return root?.channel ?? null;
};

/** Map GET /admin/partner/group-channel payload into community UI fields. */
export const PARTNER_GROUP_CHANNEL_MEMBER_PAGE_SIZE = 50;

const mapChannelMember = (member) => {
  if (typeof member === 'string') {
    const uid = member.trim();
    if (!uid) return null;
    return {
      id: uid,
      uid,
      fullName: 'Member',
      avatarUrl: undefined,
    };
  }

  if (!member || typeof member !== 'object') {
    return null;
  }

  const id = member.id ?? member.uid ?? null;
  if (id == null || String(id).trim() === '') {
    return null;
  }

  return {
    id,
    uid: member.uid ?? null,
    fullName: toTrimmedString(member.fullName ?? member.name) || 'Member',
    avatarUrl: member.avatarUrl ?? member.image ?? undefined,
    userEmail: member.userEmail ?? undefined,
    profession: member.profession ?? undefined,
    interests: Array.isArray(member.interests) ? member.interests : [],
    gender: member.gender ?? undefined,
    joinedAt: member.joinedAt ?? null,
  };
};

const mapMembersPagination = (streamData, membersLength = 0) => {
  const pagination = streamData?.membersPagination;
  if (pagination && typeof pagination === 'object') {
    const limit = Number(pagination.limit);
    const offset = Number(pagination.offset);
    return {
      limit: Number.isFinite(limit) && limit > 0 ? limit : PARTNER_GROUP_CHANNEL_MEMBER_PAGE_SIZE,
      offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
      hasMore: Boolean(pagination.hasMore),
    };
  }

  return {
    limit: membersLength || PARTNER_GROUP_CHANNEL_MEMBER_PAGE_SIZE,
    offset: 0,
    hasMore: false,
  };
};

export const mapPartnerGroupChannel = (payload) => {
  const root = payload?.data ?? payload;
  const streamData = root?.streamData ?? null;

  const channel = extractPartnerGroupChannelRoot(payload) ?? root;

  if (!channel || typeof channel !== 'object') {
    return null;
  }

  const membersSource = streamData?.members ?? [];
  const messagesSource = channel.messages ?? [];

  const members = (Array.isArray(membersSource) ? membersSource : [])
    .map(mapChannelMember)
    .filter(Boolean);

  const messages = (Array.isArray(messagesSource) ? messagesSource : [])
    .map(normalizeMessage)
    .filter(Boolean);

  const membersPagination = mapMembersPagination(streamData, members.length);
  const memberCount = Number(streamData?.memberCount);
  const resolvedMemberCount = Number.isFinite(memberCount) ? memberCount : members.length;

  return {
    id: channel.id ?? null,
    name: toTrimmedString(channel.name ?? streamData?.name) || 'Community',
    description: toTrimmedString(channel.description ?? streamData?.description) || '',
    isPrivate: (() => {
      const privacyValue = channel.accessPolicy ?? null;
      if (typeof privacyValue === 'boolean') return privacyValue;
      return String(privacyValue || '').toLowerCase() === 'paid';
    })(),
    requireJoinApproval: Boolean(channel.requireJoinApproval),
    attendeeEligibility: normalizeAttendeeEligibility(channel.attendeeEligibility),
    pendingJoinRequests: root.pendingJoinRequestCount ?? 0,
    isArchived: Boolean(channel.isArchived) || Boolean(channel.deletedAt),
    deletedAt: channel.deletedAt ?? null,
    members,
    membersPagination,
    messages,
    // Stream total (may include partner staff). Loaded `members` is one filtered page.
    memberCount: resolvedMemberCount,
    imageUrl: channel.imageUrl ?? streamData?.image ?? null,
    enabled: channel.enabled != null ? Boolean(channel.enabled) : true,
    exists: streamData?.exists ?? true,
    streamChannelId: channel.channelId ?? null,
    raw: { channel, streamData, root },
  };
};

export const mapPartnerGroupChannelResponse = (result) => {
  if (!result || result.status !== 'found') {
    return {
      status: result?.status ?? 'error',
      channel: null,
      message: result?.message ?? null,
    };
  }

  return {
    status: 'found',
    channel: mapPartnerGroupChannel(result.data),
    message: result.message ?? null,
  };
};

/**
 * Merge infinite-query channel detail pages: channel metadata from the first page,
 * members concatenated (deduped) across pages.
 */
export const buildPartnerGroupChannelFromPages = (pages = []) => {
  const foundPages = (Array.isArray(pages) ? pages : []).filter(
    (page) => page?.status === 'found' && page.channel,
  );
  if (!foundPages.length) {
    return null;
  }

  const base = foundPages[0].channel;
  const seen = new Set();
  const members = [];

  foundPages.forEach((page) => {
    (page.channel?.members ?? []).forEach((member) => {
      const key = String(member?.id ?? member?.uid ?? '').trim();
      if (!key || seen.has(key)) {
        return;
      }
      seen.add(key);
      members.push(member);
    });
  });

  const lastPagination =
    foundPages[foundPages.length - 1]?.channel?.membersPagination ?? base.membersPagination;

  return {
    ...base,
    members,
    membersPagination: lastPagination,
  };
};

/**
 * API pages arrive newest-first (desc). Reverse each page and prepend older pages
 * so chat renders oldest at top and newest at bottom.
 */
export const buildChatMessagesFromPages = (pages = []) => {
  if (!Array.isArray(pages) || pages.length === 0) return [];

  const seen = new Set();
  const ordered = [];

  pages
    .slice()
    .reverse()
    .forEach((page) => {
      const batch = Array.isArray(page?.messages) ? page.messages : [];
      [...batch].reverse().forEach((message) => {
        if (!message?.id || seen.has(message.id)) return;
        seen.add(message.id);
        ordered.push(message);
      });
    });

  return ordered;
};

/** Map GET /admin/channel/:channelId/messages page into chat UI model. */
export const mapPartnerGroupChannelMessagesPage = (result, options = {}) => {
  if (!result || result.status !== 'found') {
    return {
      status: result?.status ?? 'error',
      messages: [],
      pagination: { next: null, hasMore: false, limit: 20 },
      channelId: null,
      message: result?.message ?? null,
    };
  }

  const root = result.data?.data ?? result.data ?? {};
  const mapOptions = resolvePartnerGroupChannelMessageMapOptions(options);
  const messages = (Array.isArray(root.messages) ? root.messages : [])
    .map((message, index) => normalizeMessage(message, index, mapOptions))
    .filter(Boolean);
  const pagination = root.pagination ?? {};

  return {
    status: 'found',
    messages,
    pagination: {
      limit: Number(pagination.limit ?? 20),
      next: pagination.next ?? null,
      hasMore: Boolean(pagination.hasMore),
      order: pagination.order ?? 'desc',
      anchor: pagination.anchor ?? 'createdAt',
    },
    channelId: root.channelId ?? null,
    message: result.message ?? null,
  };
};
